import { useSigningState } from '@modusign/react-signing/setup';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import useUploadDocumentFile, {
  UploadDocumentFile,
} from '@app/hooks/documentSetup/useUploadDocumentFile';
import { usePDF } from '@app/hooks/usePDF';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import workspaceService from '@app/libs/workspaceService';
import { recordGAEvent, setDataLayerValues } from '@app/store/analytics/actions';
import { contractSetupContractDataUpdate } from '@app/store/contract-setup/actions';
import { useCheckSigningRequestAuthority } from '@app/store/contract-setup/hooks/useCheckSigningRequestAuthority';
import { initializeDocumentRenderTree } from '@app/store/document/renderTree/actions';
import { clearDocumentSetupState } from '@app/store/documentSetup/actions';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import { getFileFromURL } from '@app/store/transport-layer/files';

export default function useContractSetupDocumentFileUpload(
  startFrom: 'create-approvals' | 'documents' | 'drafts' | 'embedded'
) {
  const dispatch = useDispatch();

  const { checkAuthority } = useCheckSigningRequestAuthority();
  const history = useHistory();
  const { resetPDF, setPDF } = usePDF();
  const { initialize, signingState } = useSigningState();

  // 이벤트 기록
  const recordGA = (file: UploadDocumentFile) => {
    dispatch(recordGAEvent('문서', '업로드'));
    if (startFrom !== 'create-approvals') {
      dispatch(
        setDataLayerValues('서명요청', {
          파일형식: getExtensionFromFileName(file.name).replace('.', ''),
          업로드파일출처: [
            'url' in file && '샘플문서',
            'blob' in file && '초안',
            '일반업로드',
          ].find(Boolean),
        })
      );
    }
  };

  const startEmbeddedContractSetup = async ({
    fileId,
    title,
    url,
  }: {
    fileId: string;
    title: string;
    url: string;
  }) => {
    try {
      dispatch(modalClose());
      await checkAuthority(workspaceService.getWorkspaceId());

      resetPDF();
      const { data: pdf } = await getFileFromURL(url);
      setPDF(pdf);
      initialize({
        fields: [],
        document: {
          ...signingState.document,
          pdfFile: {
            id: fileId,
            url,
          },
        },
      });

      dispatch(contractSetupContractDataUpdate({ fileId, title, url }));
      history.push(`/editor/others-require/set?entryPoint=embedded`);
    } catch (error) {
      dispatch(modalClose());
      dispatch(
        modalOpen('CONFIRM', {
          title: '모두싸인 이용 안내',
          content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
        })
      );
    }
  };

  return useUploadDocumentFile({
    onError: (error) => {
      dispatch(modalOpen('FILE_UPLOAD_ERROR_MODAL', { error }));
    },
    onSuccess: ({ fileId, url, title }, file) => {
      recordGA(file);

      // 렌더트리 및 PDF관련 상태 초기화
      dispatch(clearDocumentSetupState());
      dispatch(initializeDocumentRenderTree());

      // eslint-disable-next-line default-case
      switch (startFrom) {
        case 'documents':
          dispatch(
            modalOpen('CONTRACT_SETUP_START_CONFIRM_MODAL', {
              fileId,
              startFrom: 'documents',
              title,
              url,
            })
          );
          break;
        case 'embedded': {
          startEmbeddedContractSetup({ fileId, title, url });
          break;
        }
        case 'create-approvals': {
          dispatch(
            modalOpen('CONTRACT_SETUP_START_CONFIRM_MODAL', {
              fileId,
              startFrom: 'create-approvals',
              title,
              url,
            })
          );
          break;
        }
      }
    },
  });
}
